import { createTheme } from '@mui/material'
import { frFR, enUS } from '@mui/material/locale'
import { bgGrey, brutRgbMainBlack, brutRgbMainBlue, brutRgbMainWhite, darkBlue, disabledColor, greyBlue, lightBlue, lightGrey, mainBlue, mainWhite, secondaryBlue, errorColor, veryLightBlue } from './ColorTheme'
import { getTextColorByBackground } from 'utils/ColorUtil'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { isNil } from 'lodash'

const MAIN_BOX_SHADOW = '0px 2px 16px 0px rgb(0 0 0 / 28%)'
const LIGHT_BOX_SHADOW = '0px 2px 6px 0px rgb(0 0 0 / 4%)'
const MAIN_RADIUS = '4px'
const LARGE_RADIUS = '10px'

const ELLIPSIS_TEXT = {
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    overflow: 'hidden',
}

const Theme = createTheme({
    palette: {
        primary: {
            main: mainBlue,
            grey: greyBlue,
            light: lightBlue,
            veryLight: veryLightBlue,
        },
        secondary: {
            main: secondaryBlue,
        },
        third: {
            main: darkBlue,
        },
        mainBlack: {
            main: '#000',
        },
        mainWhite: {
            main: mainWhite,
        },
        lightGrey: {
            contrastText: '#000',
            main: '#F5F5F5',
        },
        validStatus: {
            contrastText: '#3AA94B',
            main: '#EDFAF7',
        },
        warningStatus: {
            contrastText: '#F9BE00',
            main: '#FAF5E0',
        },
        reinforcedWarningStatus: {
            contrastText: '#F18117',
            main: '#F9EEE3',
        },
        errorStatus: {
            contrastText: errorColor,
            main: '#F8E6E5',
        },
        unknownStatus: {
            contrastText: '#EEF1F4',
            main: '#9FA0AA',
        },
        blueStatus: {
            contrastText: '#3F51B5',
            main: '#3F51B51A',
        },
        bannerColor: {
            main: mainWhite,
        },
        black60: {
            main: `rgba(${brutRgbMainBlack}, 0.6)`,
        },
        black80: {
            main: `rgba(${brutRgbMainBlack}, 0.8)`,
        },
        whiteStatus: {
            contrastText: mainBlue,
            main: mainWhite,
        },
    },
    checkbox: {},
    svgIcon: {},
    components: {
        MuiStack: {
            defaultProps: {
                useFlexGap: true,
            },
        },
        MuiPaper: {
            styleOverrides: {
                outlined: {
                    borderColor: 'grey',
                    overflow: 'hidden',
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                html: {
                    '*::-webkit-scrollbar': {
                        height: '8px',
                        width: '8px',
                    },
                    '*::-webkit-scrollbar-track': {
                        borderRadius: '20px',
                        backgroundColor: bgGrey,
                    },
                    '*::-webkit-scrollbar-thumb': {
                        borderRadius: '20px',
                        backgroundColor: lightGrey,

                        border: '1px solid transparent',
                        backgroundClip: 'content-box',
                        webkitBackgroundClip: 'content-box',
                    },
                    '*::-webkit-scrollbar-thumb:hover': {
                        backgroundColor: '#b7b7b7',

                        backgroundClip: 'border-box',
                        webkitBackgroundClip: 'border-box',
                    },
                },
                h5: {
                    margin: '0.65rem 0',
                },
            },
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    backgroundColor: lightGrey,
                    borderRadius: '10px 10px 0 0',
                    display: 'flex',
                    justifyContent: 'space-between',
                },
                indicator: {
                    display: 'none',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    color: 'black',
                    backgroundColor: lightGrey,
                    textTransform: 'none',
                    fontWeight: 400,
                    fontSize: '1.25rem',
                    borderRadius: 0,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    padding: '4px 16px',
                    '&.Mui-selected': {
                        backgroundColor: 'white',
                        color: mainBlue,
                        fontWeight: 'bold',
                        borderRadius: '10px 10px 0 0',
                        '&:hover': {
                            backgroundColor: 'white',
                            color: mainBlue,
                            opacity: 1,
                        },
                    },
                    '&:hover': {
                        backgroundColor: 'white',
                        color: mainBlue,
                        borderRadius: '10px 10px 0 0',
                        opacity: 0.75,
                    },
                },
            },
        },
        MuiMenu: {
            styleOverrides: {
                root: {
                    zIndex: 1501,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    margin: 0,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    paddingRight: 1,
                    '& input[type=text]:not(.browser-default)': {
                        margin: 0,
                        padding: !ownerState.startAdornment ? '0 0.5rem' : '0 0.15rem',
                        height: '2rem',
                    },
                    '& .MuiOutlinedInput-root': {
                        paddingRight: '0.5rem',
                    },
                }),
            },
        },
        MuiCardContent: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    padding: ownerState.padding ?? '10',
                    margin: '0',
                    ['&:last-child']: {
                        paddingBottom: ownerState.padding ?? '10',
                    },
                }),
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    const stateColor = theme.palette[ownerState.color]?.main ?? ownerState.color ?? mainWhite
                    return ({
                        padding: '5 10 5 20',
                        borderRadius: `${MAIN_RADIUS} ${MAIN_RADIUS} 0 0`,
                        minHeight: 40,
                        height: 40,
                        borderBottom: '1px solid #CCC',
                        backgroundColor: stateColor,
                        color: getTextColorByBackground(stateColor),
                        // fontWeight: ownerState.disableTypography ? undefined : 'bold',
                        // fontSize: ownerState.disableTypography ? undefined : '1.4rem',
                    })
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&:focus, &.Mui-focusVisible': { backgroundColor: 'transparent' },
                    padding: '2px',

                    variants: [
                        {
                            props: { size: 'custom' },
                            style: ({ ownerState }) => ({
                                '&:hover, &:focus, &.Mui-focusVisible': { backgroundColor: ownerState.sx?.backgroundColor ?? 'transparent' },
                            }),
                        },
                        {
                            props: { size: 'actionTable' },
                            style: {
                                padding: '1px',
                                fontSize: '18px',
                            },
                        },
                        {
                            props: { size: 'actionInputEnd' },
                            style: {
                                padding: '0 3px',
                                borderRadius: 0,
                                borderLeft: 'solid 1px rgba(0, 0, 0, 0.35)',
                                fontSize: '1.35rem',
                            },
                        },
                    ],
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    '&:before': {
                        display: 'none',
                    },
                    borderRadius: 4,
                },
            },
            defaultProps: {
                disableGutters: true,
                TransitionProps: { unmountOnExit: true, timeout: 250 },
            },
        },
        MuiAccordionSummary: {
            defaultProps: {
                expandIcon: (
                    // eslint-disable-next-line react/react-in-jsx-scope
                    <ExpandMoreIcon sx={{ fontSize: '30px', borderRadius: '50%', border: '2px solid', borderColor: 'inherit' }} />
                ),
            },
            styleOverrides: {
                root: ({ ownerState }) => ({
                    padding: '0 16 0 20',
                    color: ownerState.fontColor ?? 'white',
                    backgroundColor: ownerState.color ?? mainBlue,
                    minHeight: 40,
                    fontSize: '1.4rem',
                    fontWeight: 'bold',
                    borderRadius: ownerState.expanded ? '4px 4px 0 0' : '4px',
                    borderBottom: ownerState.color === 'white' && `solid 1px ${ownerState.fontColor ?? mainBlue}`,

                    '&.Mui-focusVisible': {
                        backgroundColor: ownerState.color ?? mainBlue,
                    },
                    '&:focus': {
                        backgroundColor: ownerState.color ?? mainBlue,
                    },
                }),
                expandIconWrapper: ({ ownerState }) => ({
                    color: ownerState.fontColor ?? 'white',
                    borderColor: ownerState.fontColor ?? 'white',
                }),
                content: {
                    margin: '0 10px 0 0',
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    borderRadius: '0 0 4px 4px',
                    padding: '5 10',
                },
            },
        },
        MuiDialog: {
            defaultProps: {
                fullWidth: true,
                scroll: 'paper',
            },
            styleOverrides: {
                paper: {
                    overflowY: 'hidden',
                    borderRadius: '5px',
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    backgroundColor: greyBlue,
                    color: 'white',
                    borderRadius: `${MAIN_RADIUS} ${MAIN_RADIUS} 0 0`,
                    borderBottom: 'solid 1px grey',
                    fontSize: '20px',
                    lineHeight: '20px',
                    '&+.MuiDialogContent-root': {
                        paddingTop: '1rem !important',
                    },
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    backgroundColor: bgGrey,
                    padding: '1rem',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    borderTop: 'solid 1px grey',
                    padding: '8px 18px',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: ({ ownerState }) => ({
                    overflow: 'visible',
                    borderLeft: ownerState.colorBorderLeft && `solid 5px ${ownerState.colorBorderLeft}`,
                    cursor: isNil(ownerState.onClick) ? undefined : 'pointer',
                    borderRadius: ownerState.borderRadius ?? MAIN_RADIUS,
                    variants: [{
                        props: { variant: 'newCard' },
                        style: {
                            border: 'solid 1px',
                            borderColor: !ownerState.selected ? `rgba(${brutRgbMainBlue}, 0.4)` : mainBlue,
                            padding: 1.5,
                            backgroundColor: '#fff',
                            borderRadius: LARGE_RADIUS,
                            boxShadow: LIGHT_BOX_SHADOW,
                            transition: 'transform .2s ease-in-out, borderColor .15s ease-in-out',
                            transform: ownerState.selected && 'scale(0.98)',
                            '&:hover': {
                                cursor: (ownerState.clickable && (!ownerState.selected || !!ownerState.deselectable)) && 'pointer',
                                transform: ownerState.clickable && `scale(${(!!ownerState.selected && !!ownerState.deselectable) ? '0.96' : '0.98'})`,
                                borderColor: ownerState.clickable && mainBlue,
                            },
                        },
                    }],
                }),
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    overflowX: 'visible',
                    overflowY: 'hidden',
                    boxShadow: 'none',
                    padding: '0',
                },
            },
        },
        MuiTable: {
            defaultProps: {
                size: 'small',
                'aria-label': 'table',
            },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    height: '23px',
                    '&:hover': {
                        '& td': {
                            backgroundColor: '#f5f5f5',
                        },
                    },
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    variants: [
                        {
                            props: { variant: 'headerIcon' },
                            style: {
                                borderColor: 'grey',
                                padding: '0',
                                width: '23px',
                                minWidth: '23px',
                                backgroundColor: '#FFF',
                                position: 'sticky',
                                zIndex: 3,
                                top: 0,
                            },
                        },
                        {
                            props: { variant: 'bodyIcon' },
                            style: {
                                padding: '1',
                                width: '21px',
                                minWidth: '21px',
                                backgroundColor: '#FFF',
                                position: 'sticky',
                            },
                        },
                    ],
                },
                head: {
                    borderColor: 'grey',
                    padding: '0',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    textWrap: 'nowrap',
                },
                body: {
                    fontSize: '13px',
                    fontWeight: 'normal',
                    padding: '1 4',
                    textWrap: 'nowrap',

                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
                paddingNone: {
                    padding: '1 0',
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                root: {
                    padding: '0 10px 0 0',
                    ['&:last-child']: {
                        padding: '0 10px 0 0',
                    },
                    paddingTop: '0px',
                },
                selectLabel: {
                    fontSize: '1.1rem',
                },
                select: {
                    fontSize: '1.1rem',
                },
                selectIcon: {
                    fontSize: '2rem',
                    color: '#555',
                },
                displayedRows: {
                    fontSize: '1.1rem',
                },
                actions: {
                    '& button': {
                        '&:focus': {
                            backgroundColor: 'white',
                        },
                    },
                    '& svg': {
                        fontSize: '2rem',
                    },
                },
            },
        },
        MuiTableSortLabel: {
            styleOverrides: {
                root: {
                    '&:hover': { color: 'rgba(0, 0, 0, 0.87)' },
                    '&:focus': { color: 'rgba(0, 0, 0, 0.87)' },
                    width: '100%',
                    padding: '6px 22px 6px 4px',
                },
                icon: {
                    margin: '0',
                },
            },
        },
        MuiList: {
            styleOverrides: {
                root: {
                    padding: '0px',
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                listbox: {
                    padding: 0,
                    maxHeight: '230px',
                    overflowY: 'auto',
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                arrow: true,
            },
            styleOverrides: {
                tooltip: {
                    maxWidth: 'fit-content',
                    fontSize: '0.9rem',
                    backgroundColor: '#292c4e',
                },
                arrow: {
                    color: '#292c4e',
                },
            },
        },
        MuiLinearProgress: {
            styleOverrides: {
                root: {
                    height: '7px',
                    borderRadius: '10px',
                    backgroundColor: '#DDD',
                },
                bar: {
                    backgroundColor: darkBlue,
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    variants: [{
                        props: { variant: 'title' },
                        style: {
                            fontSize: '1.5rem',
                            color: 'black',
                            letterSpacing: '-.25',
                        },
                    }, {
                        props: { variant: 'subtitle' },
                        style: {
                            fontSize: '1.2rem',
                            color: `rgba(${brutRgbMainBlack}, 0.6)`,
                            letterSpacing: '-.15',
                        },
                    }, {
                        props: { variant: 'formTitle' },
                        style: {
                            fontSize: '1rem',
                            color: darkBlue,
                            fontWeight: 'bold',
                        },
                    }, {
                        props: { variant: 'sectionTitle' },
                        style: {
                            fontSize: '1.2rem',
                            color: darkBlue,
                            letterSpacing: '-.15',
                        },
                    }, {
                        props: { variant: 'normalFormTitle' },
                        style: {
                            fontSize: '1rem',
                            color: darkBlue,
                            fontWeight: '400',
                        },
                    }, {
                        props: { variant: 'cardTitle' },
                        style: {
                            fontSize: '1.15rem',
                            color: darkBlue,
                            fontWeight: '600',
                        },
                    }, {
                        props: { variant: 'italicSubtitle' },
                        style: {
                            fontSize: '0.85rem',
                            color: `rgba(${brutRgbMainBlack}, 0.6)`,
                            fontWeight: '400',
                        },
                    }, {
                        props: { variant: 'legend' },
                        style: {
                            fontSize: '1rem',
                            color: darkBlue,
                            fontWeight: '400',
                        },
                    }, {
                        props: { variant: 'formError' },
                        style: {
                            fontSize: '0.85rem',
                            fontStyle: 'italic',
                            color: errorColor,
                            fontWeight: '400',
                            margin: '0.25rem 0 0 0.15rem',
                        },
                    }],
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    variants: [{
                        props: { variant: 'modern' },
                        style: {
                            borderRadius: '50rem',
                            border: `solid 2px ${mainBlue}`,
                            padding: '2px',
                            backgroundColor: '#fff',
                            overflow: 'hidden',
                            columnGap: '2px',
                        },
                    }],
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    variants: [{
                        props: { variant: 'modern' },
                        style: {
                            borderRadius: '50rem !important', // Do not remove !important! This is to manage the radius when clicking by mui.
                            padding: '2px 8px',
                            backgroundColor: '#fff',
                            color: mainBlue,
                            transition: 'all .3s ease-in-out',
                            border: 'none',
                            textTransform: 'capitalize',
                            fontSize: '1rem',
                            columnGap: '2px',
                            '&.MuiSvgIcon': {
                                fontSize: '1.25rem',
                            },
                            '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
                                backgroundColor: mainBlue,
                                color: '#fff',
                                borderRadius: '50rem',
                            },
                        },
                    }],
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    variants: [{
                        props: { variant: 'new-filled' },
                        style: ({ ownerState }) => ({
                            color: ownerState.sx?.color ?? mainWhite,
                            backgroundColor: ownerState.sx?.backgroundColor ?? `rgba(${brutRgbMainWhite}, 0.06)`,
                            transition: 'all .25s ease-in-out',
                            border: `solid 1px ${ownerState.sx?.color ?? mainWhite}`,
                            fontSize: '1rem',
                            columnGap: '4px',
                            height: 'auto',
                            padding: '4px 10px 4px 8px',
                            '& .MuiChip-label': {
                                padding: 0,
                            },
                            '& .MuiChip-icon': {
                                color: ownerState.sx?.color ?? mainWhite,
                                margin: 0,
                                fontSize: '1.25rem',
                            },
                            '&:hover': {
                                backgroundColor: ownerState.sx?.backgroundColorHover ?? `rgba(${brutRgbMainWhite}, 0.16)`,
                            },
                        }),
                    }],
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    width: 42,
                    height: 26,
                    padding: 0,
                    '& .MuiSwitch-switchBase': {
                        padding: 0,
                        margin: 2,
                        transitionDuration: '300ms',
                        '&.Mui-checked': {
                            transform: 'translateX(16px)',
                            color: '#fff',
                            '& + .MuiSwitch-track': {
                                backgroundColor: mainBlue,
                                opacity: 1,
                                border: 0,
                            },
                            '&.Mui-disabled + .MuiSwitch-track': {
                                opacity: 0.5,
                            },
                        },
                        '&.Mui-focusVisible .MuiSwitch-thumb': {
                            color: mainBlue,
                            border: `6px solid ${mainWhite}`,
                        },
                        '&.Mui-disabled .MuiSwitch-thumb': {
                            color: disabledColor,
                        },
                        '&.Mui-disabled + .MuiSwitch-track': {
                            opacity: 0.7,
                        },
                    },
                    '& .MuiSwitch-thumb': {
                        boxSizing: 'border-box',
                        width: 22,
                        height: 22,
                    },
                    '& .MuiSwitch-track': {
                        borderRadius: 26 / 2,
                        backgroundColor: lightGrey,
                        opacity: 1,
                        transition: 'background-color .5s ease-in-out',
                    },
                },
            },
        },
    },
}, navigator.language?.startsWith('fr') ? frFR : enUS)


export { Theme, MAIN_BOX_SHADOW, LIGHT_BOX_SHADOW, MAIN_RADIUS, ELLIPSIS_TEXT, LARGE_RADIUS }
